import React, { useState, useEffect, useCallback } from "react";
import { X, Flask, Copy, ArrowSquareOut, Trash, Broadcast } from "@phosphor-icons/react";
import { http, publicUrl } from "../lib/api";

export default function ExperimentsSheet({ open, onClose, deviceId, showToast }) {
  const [experiments, setExperiments] = useState([]);

  const load = useCallback(() => {
    http.get(`/experiments?device_id=${deviceId}`)
      .then((r) => setExperiments(r.data))
      .catch((e) => console.warn("Experimente laden fehlgeschlagen:", e));
  }, [deviceId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  if (!open) return null;

  const shareUrl = (exp) => publicUrl(`/experiment/${exp.share_token}`);

  const copyLink = async (exp) => {
    const link = shareUrl(exp);
    try {
      if (navigator.share) {
        await navigator.share({ title: exp.title, text: `Mein Physik-Experiment: ${exp.title}`, url: link });
        return;
      }
      await navigator.clipboard.writeText(link);
      showToast("Link kopiert – teile dein Experiment! 🧪");
    } catch (e) {
      try {
        await navigator.clipboard.writeText(link);
        showToast("Link kopiert – teile dein Experiment! 🧪");
      } catch (e2) {
        showToast(link, "info");
      }
    }
  };

  const remove = async (exp) => {
    await http.delete(`/experiments/${exp.id}?device_id=${deviceId}`).catch((e) => console.warn("Experiment löschen fehlgeschlagen:", e));
    load();
  };

  const fmtDate = (iso) => new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xl flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        data-testid="experiments-sheet"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-bg border border-white/10 rounded-t-2xl sm:rounded-2xl max-h-[85dvh] flex flex-col"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Flask size={22} weight="duotone" className="text-volt" />
            <h2 className="font-heading text-xl font-bold tracking-tight">MEINE EXPERIMENTE</h2>
          </div>
          <button data-testid="experiments-close-btn" onClick={onClose} className="p-2 rounded-full bg-elevated border border-white/10">
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
          {experiments.length === 0 && (
            <div className="text-center py-10 px-4">
              <Flask size={36} weight="duotone" className="text-muted mx-auto mb-3" />
              <p className="text-muted text-sm">
                Noch keine Experimente gespeichert.
                <br />
                Führe eine Messung durch, stelle eine Frage und tippe unter der KI-Antwort auf „Als Experiment speichern“.
              </p>
            </div>
          )}
          {experiments.map((exp) => (
            <div key={exp.id} data-testid="experiment-item" className="rounded-xl border border-white/10 bg-surface p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{exp.title}</div>
                  <div className="text-xs text-muted mt-0.5">{fmtDate(exp.created_at)} · {exp.mode === "pro" ? "Pro" : "Standard"}</div>
                </div>
              </div>
              {exp.sensor_data && exp.sensor_data.sensors && (
                <div className="flex items-center gap-1.5 text-xs text-volthover mt-2">
                  <Broadcast size={13} weight="bold" />
                  {exp.sensor_data.sensors.map((s) => s.name.split(" /")[0]).join(", ")} ({exp.sensor_data.window?.duration} s)
                </div>
              )}
              <div className="flex gap-2 mt-3">
                <button
                  data-testid="experiment-share-btn"
                  onClick={() => copyLink(exp)}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-volt/15 border border-volt/40 text-volthover rounded-lg py-2 text-xs font-semibold"
                >
                  <Copy size={14} /> Link teilen
                </button>
                <button
                  data-testid="experiment-open-btn"
                  onClick={() => window.open(shareUrl(exp), "_blank")}
                  className="flex items-center justify-center gap-1.5 bg-elevated border border-white/10 rounded-lg px-3 py-2 text-xs font-semibold"
                >
                  <ArrowSquareOut size={14} /> Öffnen
                </button>
                <button
                  data-testid="experiment-delete-btn"
                  onClick={() => remove(exp)}
                  className="flex items-center justify-center bg-elevated border border-white/10 rounded-lg px-3 py-2 text-muted hover:text-red-400"
                >
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
